import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export type ManagerNotificationPair = {
  reservation_a: { id: string; date: string; start_time: string; customer_id: string | null };
  reservation_b: { id: string; date: string; start_time: string; customer_id: string | null };
  shared_table_ids: string[];
};

export type ManagerNotificationPayload = {
  pairs: ManagerNotificationPair[];
  detected_at: string;
  total_pairs: number;
};

export type ManagerNotification = {
  id: string;
  establishment_id: string;
  type: string;
  title: string;
  body: string | null;
  payload: ManagerNotificationPayload | null;
  severity: string;
  created_at: string;
  dismissed_at: string | null;
  dismissed_by: string | null;
};

export function useManagerNotifications(establishmentId: string | null | undefined) {
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: ["manager_notifications", establishmentId, "active"],
    enabled: !!establishmentId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("manager_notifications")
        .select("*")
        .eq("establishment_id", establishmentId!)
        .is("dismissed_at", null)
        .order("created_at", { ascending: true });
      if (error) throw error;
      return (data ?? []) as unknown as ManagerNotification[];
    },
  });

  const dismiss = useMutation({
    mutationFn: async (id: string) => {
      const { data: { user } } = await supabase.auth.getUser();
      const { error } = await supabase
        .from("manager_notifications")
        .update({ dismissed_at: new Date().toISOString(), dismissed_by: user?.id ?? null })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["manager_notifications"] });
    },
  });

  return {
    notifications: query.data ?? [],
    isLoading: query.isLoading,
    dismiss: dismiss.mutateAsync,
    isDismissing: dismiss.isPending,
  };
}
